import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as core from '../../core'

/**
 * Represents the various types of external institutions that the given
 * institution might interact with, such as non-profit organizations, for-profit
 * companies, local governments, etc.
 *
 * __NOTE:__ this does not include federal agencies
 */
export enum InstitutionType {
  Foreign = 'Foreign',
  ForProfitOther = 'ForProfit-Other',
  ForProfitSmallBiz = 'ForProfit-SmallBiz',
  GovtCounty = 'Govt-County',
  GovtIsd = 'Govt-ISD',
  GovtMunicipal = 'Govt-Municipal',
  GovtPih = 'Govt-PIH',
  GovtSpecialDistrict = 'Govt-SpecialDistrict',
  GovtTerritory = 'Govt-Territory',
  HigherEdHbcu = 'HigherEd-HBCU',
  HigherEdPrivate = 'HigherEd-Private',
  HigherEdPublic = 'HigherEd-Public',
  HigherEdTccu = 'HigherEd-TCCU',
  Individual = 'Individual',
  /**
   * @see {@link https://en.wikipedia.org/wiki/Regional_organization}
   */
  IntlRegional = 'Intl-Regional',
  NativeTribalDesignee = 'Native-TribalDesignee',
  NativeTribalFederal = 'Native-TribalFederal',
  NativeTribalOther = 'Native-TribalOther',
  NonProfit501c3 = 'NonProfit-501c3',
  NonProfitOther = 'NonProfit-Other',
  Other = 'Other',
  ServingHispanic = 'Serving-Hispanic',
  ServingNativeAlaskaHawaii = 'Serving-NativeAlaskaHawaii',
}
// Needs to be before namespace is defined
const INSTITUTION_TYPES: ReadonlySet<InstitutionType> = new Set(
  Object.values(InstitutionType) as InstitutionType[],
)
export namespace InstitutionType {
  export const ALL: readonly InstitutionType[] = Array.from(INSTITUTION_TYPES)
  export const isInstitutionType = TypeChecker.Factory.oneOf(INSTITUTION_TYPES)
  export const assertIsInstitutionType: TypeAsserter<InstitutionType> =
    TypeAsserter.Factory.from(isInstitutionType)

  /**
   * Returns the {@link core.common.value.types.Enum.Entry} for the given
   * {@link InstitutionType}
   */
  export function getEntry(
    jobType: InstitutionType,
  ): core.common.value.types.Enum.Entry {
    return { ...ENTRY_LOOKUP[jobType], id: jobType }
  }
  /** Used by {@link getEntry} */
  const ENTRY_LOOKUP: Record<
    InstitutionType,
    Omit<core.common.value.types.Enum.Entry, 'id'>
  > = {
    [InstitutionType.Foreign]: {
      name: 'Non-Domestic (non-US) Entity',
    },
    [InstitutionType.ForProfitOther]: {
      name: 'For Profit Organization (other than small business)',
    },
    [InstitutionType.ForProfitSmallBiz]: {
      name: 'Small Business',
    },
    [InstitutionType.GovtCounty]: {
      name: 'County Government',
    },
    [InstitutionType.GovtIsd]: {
      name: 'Independent School District',
    },
    [InstitutionType.GovtMunicipal]: {
      name: 'City/Township Government',
    },
    [InstitutionType.GovtPih]: {
      name: 'Public/Indian Housing Authority',
    },
    [InstitutionType.GovtSpecialDistrict]: {
      name: 'Special District Government',
    },
    [InstitutionType.GovtTerritory]: {
      name: 'US Territory or Possession',
    },
    [InstitutionType.HigherEdHbcu]: {
      name: 'Historically Black Colleges & Universities',
    },
    [InstitutionType.HigherEdPrivate]: {
      name: 'Private Institution of Higher Education',
    },
    [InstitutionType.HigherEdPublic]: {
      name: 'Public/State Controlled Institution of Higher Education',
    },
    [InstitutionType.HigherEdTccu]: {
      name: 'Tribally Controlled Colleges and Universities (TCCUs)',
    },
    [InstitutionType.Individual]: {
      name: 'Individual',
    },
    [InstitutionType.IntlRegional]: {
      name: 'Regional Organization',
    },
    [InstitutionType.NativeTribalDesignee]: {
      name: 'Native American Tribally Designated Organization',
    },
    [InstitutionType.NativeTribalFederal]: {
      name: 'Native American Tribal Government (Federally Recognized)',
    },
    [InstitutionType.NativeTribalOther]: {
      name: 'Native American Tribal Government (Other than Federally Recognized)',
    },
    [InstitutionType.NonProfit501c3]: {
      name: 'Nonprofit with 501c3 Status (other than Institution of Higher Education)',
    },
    [InstitutionType.NonProfitOther]: {
      name: 'Nonprofit without 501c3 Status (other than Institution of Higher Education)',
    },
    [InstitutionType.Other]: {
      name: 'Other',
    },
    [InstitutionType.ServingHispanic]: {
      name: 'Hispanic Serving Institution',
    },
    [InstitutionType.ServingNativeAlaskaHawaii]: {
      name: 'Alaska Native & Native Hawaiian Serving Institutions',
    },
  }
}
